import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { BottomNav } from '@parsina/framework';
import { useTranslation } from '../i18n';
import { NAV_ITEMS } from '../constants/navItems.jsx';
import { bottomNavPlan, activeNavId, MORE_ID } from './bottomNav';

/**
 * The phone's primary navigation.
 *
 * Labels and icons come from `NAV_ITEMS` — the sidebar's own list — for the
 * same reason `bottomNavPlan` reads `visibleNavIds`: if «صندوق» is ever
 * renamed or re-iconed, one edit must move both surfaces. The bar contributes
 * only the fold, the count badge and the active derivation.
 *
 * The kit renders `badge` verbatim (the digit seam belongs to the app, not the
 * kit), so the count is capped and localised before it is handed over —
 * otherwise «۱۲۴» would render inside a bubble sized for one glyph.
 *
 * See docs/MOBILE-REDESIGN.md §3.2.
 */
export default function MobileBottomNav({
  access,
  barePath,
  pendingCount = 0,
  onNavigate,
  onOpenMore,
}) {
  const { t, formatDigits } = useTranslation();

  const plan = React.useMemo(() => bottomNavPlan(access), [access]);
  const navItems = React.useMemo(() => NAV_ITEMS(t), [t]);

  if (!plan.items.length) return null;

  const byId = new Map(navItems.map((n) => [n.id, n]));

  const items = plan.items.map((id) => {
    if (id === MORE_ID) {
      return {
        key: MORE_ID,
        label: t('nav.mobile.more'),
        icon: <MoreHorizontal size={20} />,
        // The fold carries the invitation count, because «دعوت‌ها» lives
        // behind it. A count on a destination nobody can see from the bar is
        // the only honest place to put it.
        badge: pendingCount > 0 ? cap(pendingCount, formatDigits) : undefined,
        badgeTone: 'count',
        badgeLabel: pendingCount > 0 ? t('nav.items.invitations') : undefined,
      };
    }
    const entry = byId.get(id);
    return { key: id, label: entry?.label || id, icon: entry?.icon };
  });

  return (
    <BottomNav
      items={items}
      active={activeNavId(barePath, plan)}
      onSelect={(key) => (key === MORE_ID ? onOpenMore() : onNavigate(key))}
      data-testid="mobile-bottom-nav"
    />
  );
}

/** «+۹۹» beyond two digits: a bubble sized for one glyph cannot hold a count. */
function cap(n, formatDigits) {
  return n > 99 ? `+${formatDigits(99)}` : formatDigits(n);
}
